import React, { useState, useEffect } from 'react';
import { Phone, Mail, MapPin, Inbox, Bot, LayoutGrid, Star, ShieldCheck, Clock, Users, ArrowRight, CalendarCheck, FileText, CheckCircle2, Check, Calculator, TrendingDown, PhoneCall } from 'lucide-react';
const BOOKING_URL = 'https://api.viridiaanalytics.com/widget/booking/hv5pIfS27zbJCMMGNnkJ';
const PHONE_DISPLAY = '(623) 300-0708';
const PHONE_TEL = 'tel:+16233000708';
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/dBgonnHgNocdZqWCx2rT29/VA-Logo_(1).png';
const HUBSPOT_BADGE = 'https://cdn.magicpatterns.com/uploads/it67T9N3RYnBZ32NVuR62H/SEOcertified.png';
const CHAMBER_BADGE = 'https://cdn.magicpatterns.com/uploads/8HQjTNAJm388sm9my7xsCp/Scotsscommerce.png';
const MISS_RATE = 0.25;
const WEEKS_PER_MONTH = 4.33;
function trackLead() {
  const w = window as any;
  if (typeof w.fbq === 'function') w.fbq('track', 'Lead');
}
function usd(n: number) {
  return '$' + Math.round(n).toLocaleString('en-US');
}
function BookCTA({
  label = 'Book Your Free 15-Minute Call',
  full = false
}: {
  label?: string;
  full?: boolean;
}) {
  return <a href={BOOKING_URL} onClick={trackLead} target="_blank" rel="noopener noreferrer" className={`group inline-flex items-center justify-center gap-2 rounded-md bg-emerald-500 px-7 py-4 text-sm font-bold text-black transition-colors hover:bg-emerald-400 ${full ? 'w-full' : ''}`} data-id="element-0" data-mp="3mlavl1vse5rc">
      {label}
      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" data-id="element-1" data-mp="2zm4xcjfp5b1a" />
    </a>;
}

/* Calling is the lower-friction ask for this audience -- contractors reach for the
   phone before they fill in a form. The number is the GHL line, so the AI picks up:
   the call itself is a live demo of the product. Fires the same Lead event so both
   paths are measurable. */
function CallCTA({
  full = false
}: {
  full?: boolean;
}) {
  return <a href={PHONE_TEL} onClick={trackLead} className={`inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-emerald-500/60 bg-transparent px-5 py-4 text-sm font-bold tracking-tight text-emerald-300 transition-colors hover:bg-emerald-500/10 ${full ? 'w-full' : ''}`} data-id="element-2" data-mp="suncc5gqgafj">
      <PhoneCall className="h-4 w-4 flex-none" data-id="element-3" data-mp="d57d4waue6fw" />
      Call {PHONE_DISPLAY}
    </a>;
}
function Eyebrow({
  children
}: {
  children: React.ReactNode;
}) {
  return <div className="font-eyebrow inline-block text-[11px] font-semibold uppercase text-emerald-500" data-id="element-4" data-mp="83qgxe3az85n">{children}</div>;
}
function GuaranteeBadge({
  big = false
}: {
  big?: boolean;
}) {
  return <div className={`inline-flex items-center gap-2 rounded-full border-2 border-emerald-500/60 bg-emerald-500/15 font-extrabold uppercase tracking-wide text-emerald-300 shadow-[0_0_40px_-10px_rgba(16,185,129,0.7)] ${big ? 'px-6 py-3 text-base' : 'px-5 py-2.5 text-sm'}`} data-id="element-5" data-mp="3bk718hkvi07g">
      <ShieldCheck className={big ? 'h-6 w-6' : 'h-5 w-5'} data-id="element-6" data-mp="2xtlo79i3okhh" /> 14-Day Money-Back Guarantee
    </div>;
}
function StatTile({
  n,
  l
}: {
  n: string;
  l: string;
}) {
  return <div className="rounded-xl border border-hairline2 bg-ink p-6 text-center sm:text-left" data-id="element-7" data-mp="18dhq9zh8ks3w">
      <div className="text-3xl font-extrabold text-emerald-400" data-id="element-8" data-mp="1d4zpoj3m2kmr">{n}</div>
      <div className="mt-2 text-xs leading-relaxed text-neutral-400" data-id="element-9" data-mp="yflj1irf6hb2">{l}</div>
    </div>;
}

/* Moved OUT of the hero (2026-08-12). It was the first thing a visitor met on
   mobile -- two empty inputs asking them to do math before they knew what this
   was. 125 landing views produced zero CTA clicks with it gating the top of the
   page. It stays as a tool for people who scroll, not as the entry ask. */
function CostCalculator() {
  const [avgJob, setAvgJob] = useState('');
  const [newCallsPerWeek, setNewCallsPerWeek] = useState('');
  const avg = parseFloat(avgJob.replace(/[^0-9.]/g, '')) || 0;
  const nc = parseFloat(newCallsPerWeek.replace(/[^0-9.]/g, '')) || 0;
  const missedPerMonth = Math.round(nc * WEEKS_PER_MONTH * MISS_RATE);
  const opportunity = missedPerMonth * avg;
  const hasEstimate = avg > 0 && nc > 0;
  return <div className="mx-auto mt-10 w-full max-w-lg rounded-2xl border border-hairline2 bg-inkAlt p-6 sm:p-8" data-id="element-10" data-mp="qy714ulemp5e">
      <div className="flex items-center gap-2" data-id="element-11" data-mp="m4ugswnrd4ig">
        <Calculator className="h-4 w-4 text-emerald-500" data-id="element-12" data-mp="h72q25yunofu" />
        <div className="font-eyebrow text-[10px] font-semibold uppercase text-emerald-500" data-id="element-13" data-mp="h7lwoqj1uqek">Do the math on your own shop</div>
      </div>
      <h3 className="mt-2 text-xl font-bold leading-snug text-white" data-id="element-14" data-mp="19vzt51uhpeo0">See what missed calls are costing you.</h3>
      <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2" data-id="element-15" data-mp="14wewiy2slieg">
        <label className="block" data-id="element-16" data-mp="14uu53hbryfkt">
          <span className="text-xs font-medium text-neutral-400" data-id="element-17" data-mp="zxamk581ngn2">Average job value</span>
          <div className="mt-1 flex items-center rounded-md border border-hairline2 bg-ink px-3 focus-within:border-emerald-500/60" data-id="element-18" data-mp="vwicj9rcl7pq">
            <span className="text-sm text-neutral-500" data-id="element-19" data-mp="3kouc73qb1e64">$</span>
            <input type="text" inputMode="numeric" value={avgJob} onChange={e => setAvgJob(e.target.value.replace(/[^0-9.]/g, ''))} placeholder="2,500" aria-label="Average job value" className="w-full bg-transparent px-2 py-3 text-sm text-white placeholder-neutral-600 outline-none" data-id="element-20" data-mp="3gmpjc77oklkj" />
          </div>
        </label>
        <label className="block" data-id="element-21" data-mp="254v2vlv3tymn">
          <span className="text-xs font-medium text-neutral-400" data-id="element-22" data-mp="253jx5gi3mgsw">New customer calls / week</span>
          <input type="text" inputMode="numeric" value={newCallsPerWeek} onChange={e => setNewCallsPerWeek(e.target.value.replace(/[^0-9.]/g, ''))} placeholder="15" aria-label="New customer calls per week" className="mt-1 w-full rounded-md border border-hairline2 bg-ink px-4 py-3 text-sm text-white placeholder-neutral-600 outline-none focus:border-emerald-500/60" data-id="element-23" data-mp="29xeg2hpvqrqx" />
        </label>
      </div>

      {hasEstimate ? <div className="mt-6 rounded-xl border border-emerald-500/40 bg-emerald-500/[0.07] p-6 text-center" data-id="element-24" data-mp="2sslb1j9pak5k">
          <div className="flex items-center justify-center gap-2 text-emerald-400" data-id="element-25" data-mp="tjxzmrwc1yjt">
            <TrendingDown className="h-4 w-4" data-id="element-26" data-mp="yir6w2l10tbj" />
            <span className="text-xs font-semibold uppercase tracking-wide" data-id="element-27" data-mp="21qskmeiwm8jd">Walking out your door every month</span>
          </div>
          <div className="mt-2 text-5xl font-extrabold tracking-tight text-white sm:text-6xl" data-id="element-28" data-mp="1wu26hwqntn0l">{usd(opportunity)}</div>
          <p className="mt-3 text-sm leading-relaxed text-neutral-300" data-id="element-29" data-mp="2aq5wfdui3979">
            That's ~<span className="font-bold text-white" data-id="element-30" data-mp="25rm9pb831tdu">{missedPerMonth} new-customer calls</span> you miss a month at {usd(avg)} a job. Even if you'd only close a third, that's still <span className="font-bold text-white" data-id="element-31" data-mp="2fs506ou5k84q">{usd(opportunity * 0.33)}/month</span> gone to voicemail.
          </p>
          <div className="mt-6 grid grid-cols-1 gap-2 sm:grid-cols-2" data-id="element-32" data-mp="2thgkzocr79vz">
            <BookCTA label="Book a call" full data-id="element-33" data-mp="2pgmy8yb1s14o" />
            <CallCTA full data-id="element-34" data-mp="2phy89fv36548" />
          </div>
          <p className="mt-2 text-[11px] text-neutral-500" data-id="element-35" data-mp="33geg5a4oq1ny">Estimate, based on the industry-typical 1-in-4 missed-call rate.</p>
        </div> : <p className="mt-5 text-xs text-neutral-500" data-id="element-36" data-mp="2yjebn3nhh6yy">Enter your two numbers to see the number nobody wants to see.</p>}
    </div>;
}
const PROOF_SHOTS: {
  src: string;
  alt: string;
  caption: string;
  wide?: boolean;
}[] = [{
  src: 'https://cdn.magicpatterns.com/uploads/wWqj2HFbXWoSbhqbiUJ8vZ/tfpcalendar.png',
  alt: 'Client calendar week filled with AI-booked estimate appointments',
  caption: 'A full week of estimates — every morning and midday slot booked by the AI, hands-free, while the owner was out on jobs.',
  wide: true
}, {
  src: 'https://cdn.magicpatterns.com/uploads/nAd889CKQBqMghTE6qEF6o/gomezcalendar.png',
  alt: 'Client calendar week with morning and midday appointments booked by the AI',
  caption: 'A different account, a different week — back-to-back appointments, every lead answered in seconds. No receptionist.',
  wide: true
}, {
  src: 'https://cdn.magicpatterns.com/uploads/m3aRyYsbUmc6Pak81FhEmK/tfpcontacts.png',
  alt: 'CRM contact list showing 265 captured leads with service type and created date',
  caption: '265 leads captured — each tagged with the job they need and the minute they came in.'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/mwws2uujakBnnh9qHKxTwY/gomezleads.png',
  alt: 'Lead list showing contact name, service needed, and created date',
  caption: 'Tree service, irrigation, landscape design, lawn care — none of it left sitting in a voicemail box.'
}];
const FLOW_SOURCES = ['Missed call', 'Yelp', 'Google', 'Facebook', 'Web form'];
const FLOW_STAGES = [{
  icon: Inbox,
  label: 'Lead captured',
  sub: 'Every call & message'
}, {
  icon: CalendarCheck,
  label: 'Booked',
  sub: 'On your calendar'
}, {
  icon: FileText,
  label: 'Invoice sent',
  sub: 'QuickBooks synced'
}, {
  icon: CheckCircle2,
  label: 'Job completed',
  sub: 'Tracked to done'
}, {
  icon: Star,
  label: 'Review requested',
  sub: 'Your reputation grows'
}];
function FlowNode({
  stage,
  state
}: {
  stage: (typeof FLOW_STAGES)[number];
  state: 'idle' | 'current' | 'done';
}) {
  const Icon = stage.icon;
  const done = state === 'done';
  const current = state === 'current';
  return <div className="flex w-28 flex-none flex-col items-center text-center sm:w-32" data-id="element-37" data-mp="dpq3zabw1vhj">
      <div className="relative" data-id="element-38" data-mp="ipubiautjfvm">
        {current && <span className="absolute inset-0 rounded-full bg-emerald-500/30 animate-[vaPing_1.6s_ease-out_infinite]" data-id="element-39" data-mp="iq3zjswazcyo" />}
        <div className={`relative flex h-12 w-12 items-center justify-center rounded-full border transition-all duration-500 ${done ? 'border-emerald-500 bg-emerald-500 text-black' : current ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400' : 'border-hairline2 bg-inkAlt text-neutral-600'}`} data-id="element-40" data-mp="nml05mkovjsn">
          {done ? <Check className="h-5 w-5" strokeWidth={2.5} data-id="element-41" data-mp="3w2nxaye6dq2q" /> : <Icon className="h-5 w-5" strokeWidth={2} data-id="element-42" data-mp="4quw7tt74dc4" />}
        </div>
      </div>
      <div className={`mt-3 text-sm font-semibold transition-colors duration-500 ${done || current ? 'text-white' : 'text-neutral-500'}`} data-id="element-43" data-mp="33gbjls1ncnbf">{stage.label}</div>
      <div className={`mt-0.5 text-[11px] leading-tight transition-colors duration-500 ${done || current ? 'text-neutral-400' : 'text-neutral-600'}`} data-id="element-44" data-mp="38ctkuhxrq3j0">{stage.sub}</div>
    </div>;
}
function FlowConnector({
  filled
}: {
  filled: boolean;
}) {
  return <div className="flex items-center justify-center py-1 md:flex-1 md:px-1 md:py-0" data-id="element-45" data-mp="2p51itmgnkq9m">
      <div className="relative h-7 w-px overflow-hidden rounded bg-hairline2 md:mt-6 md:h-px md:w-full" data-id="element-46" data-mp="2u4xmtlpcohp6">
        <div className={`absolute inset-0 origin-top rounded bg-emerald-500 transition-transform duration-700 ease-out md:origin-left ${filled ? 'scale-y-100 md:scale-x-100' : 'scale-y-0 md:scale-x-0'} scale-x-100 md:scale-y-100`} data-id="element-47" data-mp="1whf2req8nt5v" />
      </div>
    </div>;
}
function LeadFlow() {
  const [active, setActive] = useState(0);
  useEffect(() => {
    const reduce = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      setActive(FLOW_STAGES.length);
      return;
    }
    const id = setInterval(() => setActive(a => a >= FLOW_STAGES.length ? 0 : a + 1), 1150);
    return () => clearInterval(id);
  }, []);
  const capturing = active === 0;
  return <div className="relative mt-10 overflow-hidden rounded-xl border border-hairline2 bg-inkAlt p-6 sm:p-10" data-id="element-48" data-mp="3ryf25ctopzm4">
      <style data-id="element-49" data-mp="3mwzh3kribt3q">{`@keyframes vaPing{0%{transform:scale(1);opacity:.5}70%,100%{transform:scale(2);opacity:0}}`}</style>
      <div className="pointer-events-none absolute inset-0 opacity-60" style={{
      background: 'radial-gradient(55% 60% at 50% 38%, rgba(15,176,125,0.12) 0%, rgba(10,10,10,0) 72%)'
    }} data-id="element-50" data-mp="3i1tzgy5510lg" />
      <div className="relative" data-id="element-51" data-mp="388mtbb2kpgkb">
        <div className="font-eyebrow text-center text-[10px] font-semibold uppercase text-neutral-500" data-id="element-52" data-mp="348cera0pthtu">Leads come in from</div>
        <div className="mt-3 flex flex-wrap items-center justify-center gap-2" data-id="element-53" data-mp="2za144x2lj3ak">
          {FLOW_SOURCES.map(s => <span key={s} className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors duration-500 ${capturing ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-300' : 'border-hairline2 bg-ink text-neutral-500'}`} data-id="element-54" data-mp="2u8tx508a284y">
              <span className={`h-1.5 w-1.5 rounded-full transition-colors duration-500 ${capturing ? 'bg-emerald-400' : 'bg-neutral-600'}`} data-id="element-55" data-mp="2kmsy0ao59o5g" />{s}
            </span>)}
        </div>
        <div className="mx-auto mt-3 h-5 w-px bg-gradient-to-b from-emerald-500/60 to-transparent" data-id="element-56" data-mp="2antmmkbzveer" />
        <div className="mt-2 flex flex-col items-center md:flex-row md:items-start md:justify-center" data-id="element-57" data-mp="2ydfmzb43rzoc">
          {FLOW_STAGES.map((stage, i) => {
          const state: 'idle' | 'current' | 'done' = active > i ? 'done' : active === i ? 'current' : 'idle';
          return <React.Fragment key={stage.label} data-id="element-58" data-mp="2pjt3okh30ow1" data-mpi={i}><FlowNode stage={stage} state={state} data-id="element-59" data-mp="2tmpjy4j5g8wt" />{i < FLOW_STAGES.length - 1 && <FlowConnector filled={active > i} data-id="element-60" data-mp="2tkuxumw56ar5" />}</React.Fragment>;
        })}
        </div>
        <div className="mt-8 text-center text-xs text-neutral-500" data-id="element-61" data-mp="3hayw0eqk9iar">One system runs the whole job, start to finish. You just show up and do the work.</div>
      </div>
    </div>;
}
export function App() {
  return <div className="min-h-full w-full bg-ink text-neutral-100" data-id="element-62" data-mp="15pq0mnwuerq7">
      {/* 1. HERO -- headline matches the winning ad verbatim ("We answer every lead,
             or you don't pay"), one line of what it is, then TWO ways to act.
             No form, no inputs, nothing to figure out. */}
      <header className="relative overflow-hidden border-b border-hairline" data-id="element-63" data-mp="rpp1bcw87scu">
        <div className="pointer-events-none absolute inset-0 opacity-40" style={{
        background: 'radial-gradient(60% 50% at 50% 0%, rgba(15,176,125,0.16) 0%, rgba(10,10,10,0) 70%)'
      }} data-id="element-64" data-mp="rrrpdrdtjbex" />
        <div className="relative mx-auto flex max-w-3xl flex-col items-center px-5 pb-14 pt-10 text-center sm:pb-18 sm:pt-14" data-id="element-65" data-mp="11pfvfdgvk5ce">
          <img src={LOGO_URL} alt="Viridia Analytics" className="h-16 w-auto sm:h-24" data-id="element-66" data-mp="wr4lz3uhrwup" />
          <div className="mt-8" data-id="element-67" data-mp="2d54i03q7v2co"><Eyebrow data-id="element-68" data-mp="283xbe7zt897l">Phoenix Home-Service Owners</Eyebrow></div>
          <h1 className="mt-4 text-[2.2rem] font-extrabold leading-[1.1] tracking-tight text-white sm:text-5xl" data-id="element-69" data-mp="283nk8h313w0b">
            We answer every lead, or you don't pay.
          </h1>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-neutral-400 sm:text-lg" data-id="element-70" data-mp="mmpjjf6k8zs9">
            Our AI picks up every call, text, and Yelp lead in seconds and books the job on your
            calendar. Even when you're on a roof at 7pm.
          </p>

          <div className="mt-8 grid w-full max-w-md grid-cols-1 gap-3 sm:grid-cols-2" data-id="element-71" data-mp="10lxjiaajsbxi">
            <BookCTA label="Book a 15-Min Call" full data-id="element-72" data-mp="15kscu6655ivv" />
            <CallCTA full data-id="element-73" data-mp="15j7jgjvkc5zl" />
          </div>
          <p className="mt-3 text-xs text-neutral-500" data-id="element-74" data-mp="1abh3jj9zybty">
            Call right now and our AI will answer. That's the whole product, live.
          </p>

          <div className="mt-7" data-id="element-75" data-mp="1ebsrb4xnos9n"><GuaranteeBadge big data-id="element-76" data-mp="1ef6qy5exqnde" /></div>
        </div>
      </header>

      {/* 2. THE PAIN */}
      <section className="border-b border-hairline bg-inkAlt" data-id="element-77" data-mp="1je04tyy4wge3">
        <div className="mx-auto max-w-4xl px-5 py-14 sm:py-20" data-id="element-78" data-mp="1oagr0nlrwkoj">
          <Eyebrow data-id="element-79" data-mp="1yf715grbyv3u">What a missed call actually costs you</Eyebrow>
          <h2 className="mt-3 max-w-2xl text-2xl font-bold leading-snug text-white sm:text-3xl" data-id="element-80" data-mp="1ygi2l9xazqrs">
            The caller doesn't leave a voicemail. They just call the next name on Google.
          </h2>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-neutral-400" data-id="element-81" data-mp="1ohapmnuhdy57">
            A busy shop misses 1 out of every 4 calls. At a few thousand dollars a job, that's tens of
            thousands walking out the door every month — leads you already paid Google, Yelp, and Meta to send you.
          </p>
          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-82" data-mp="2c8xy7pnvkmio">
            {["You're on a rooftop swapping a condenser and the phone buzzes in your pocket.", "You're under a sink mid-repair with wet hands and no way to grab it.", "You're driving between jobs and it goes straight to voicemail.", "You're mid-estimate with one customer while a second lead is calling right now."].map((line, i) => <div key={i} className="rounded-lg border border-hairline bg-ink p-5 text-sm leading-relaxed text-neutral-400" data-id="element-83" data-mp="t8p9e1e4e3h0" data-mpi={i}>{line}</div>)}
          </div>
          <CostCalculator data-id="element-84" data-mp="jh2qrjzgd248" />
        </div>
      </section>

      {/* 3. THE FIX */}
      <section className="border-b border-hairline" data-id="element-85" data-mp="ogp2tq3jocbg">
        <div className="mx-auto max-w-5xl px-5 py-14 sm:py-20" data-id="element-86" data-mp="ofdxhlugawi8">
          <Eyebrow data-id="element-87" data-mp="ageaeiml6po6">From missed call to five-star review</Eyebrow>
          <h2 className="mt-3 max-w-2xl text-2xl font-bold leading-snug text-white sm:text-3xl" data-id="element-88" data-mp="fg92uqm0fnbc">
            One lead comes in. The system runs the entire job for you.
          </h2>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-neutral-400" data-id="element-89" data-mp="lmgbos7qky5">
            Every call, text, and message from every platform lands in one place, then the system carries
            it the whole way — booked, invoiced, completed, and reviewed. Nothing slips.
          </p>
          <LeadFlow data-id="element-90" data-mp="1zu9ng3yjske3" />
          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-91" data-mp="3keekws0t74aw">
            {[{
            icon: Bot,
            title: 'Instant response to every lead',
            body: 'An AI receptionist answers every call, text, and message in seconds, 24/7, before they dial the next name.'
          }, {
            icon: Inbox,
            title: 'One unified inbox',
            body: 'Every call, text, Yelp, Google, and Facebook message lands in one place, not five apps.'
          }, {
            icon: LayoutGrid,
            title: 'Branded CRM + mobile app',
            body: 'Run the whole thing from your phone. It chases every lead until they book or say no.'
          }, {
            icon: Star,
            title: 'Reviews + QuickBooks-synced invoicing',
            body: 'Reputation and invoicing run in the background while you run the job. Google Business Profile set up and optimized.'
          }].map(({
            icon: Icon,
            title,
            body
          }, i) => <div key={i} className="rounded-lg border border-hairline2 bg-inkAlt p-6" data-id="element-92" data-mp="2ipkrzr71m963" data-mpi={i}>
                <Icon className="h-5 w-5 text-emerald-500" strokeWidth={2} data-id="element-93" data-mp="2dr9hrfcu5wnc" />
                <h3 className="mt-4 text-base font-semibold text-white" data-id="element-94" data-mp="1rxkbxe5fgy54">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-neutral-400" data-id="element-95" data-mp="1wvdii8an7u5x">{body}</p>
              </div>)}
          </div>
          <div className="mt-10 flex flex-col items-center gap-3 sm:flex-row sm:justify-center" data-id="element-96" data-mp="21nwux1xlgj95">
            <BookCTA label="Book a 15-Min Call" data-id="element-97" data-mp="26kdl01oct7ov" />
            <CallCTA data-id="element-98" data-mp="18xy3x0zuac1u" />
          </div>
        </div>
      </section>

      {/* 3b. THE DARE -- the strongest thing on the page.
             Every visitor is silently thinking "AI can't actually sell." Rather
             than argue, hand them the phone: the number is the live GHL line, so
             the skeptic gets handled by the exact system we'd install for them. */}
      <section className="relative overflow-hidden border-b border-hairline bg-inkAlt" data-id="element-99" data-mp="1nt48djjae2t2">
        <div className="pointer-events-none absolute inset-0 opacity-70" style={{
        background: 'radial-gradient(60% 70% at 50% 50%, rgba(15,176,125,0.14) 0%, rgba(10,10,10,0) 70%)'
      }} data-id="element-100" data-mp="2uazr3nesshw8" />
        <div className="relative mx-auto max-w-3xl px-5 py-16 text-center sm:py-24" data-id="element-101" data-mp="nlpg793aep6t">
          <Eyebrow data-id="element-102" data-mp="ioz04lrhftle">Don't take our word for it</Eyebrow>
          <h2 className="mt-4 text-3xl font-extrabold leading-[1.15] tracking-tight text-white sm:text-4xl" data-id="element-103" data-mp="dx7i4zme6w6v">
            Think AI won't close?<br className="hidden sm:block" data-id="element-104" data-mp="8yw92r3x8ppp" /> Call it right now.
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-neutral-300" data-id="element-105" data-mp="3yrqmvjgdkwu">
            The number below isn't a sales line. It's the exact system we'd put on your phone,
            answering live. Try to stump it. Ask what it costs. Ask if it's a robot.
          </p>

          <div className="mx-auto mt-8 max-w-md" data-id="element-106" data-mp="3r5k9i3o10op1">
            <a href={PHONE_TEL} onClick={trackLead} className="group flex w-full items-center justify-center gap-3 rounded-md bg-emerald-500 px-7 py-5 text-lg font-extrabold tracking-tight text-black transition-colors hover:bg-emerald-400" data-id="element-107" data-mp="3m5xym0vo7kje">
              <PhoneCall className="h-5 w-5 flex-none" data-id="element-108" data-mp="2c6ayhmu46hi6" />
              Call {PHONE_DISPLAY}
            </a>
            <p className="mt-3 text-xs text-neutral-500" data-id="element-109" data-mp="279kforqxd7t2">
              Answers in seconds, 24/7 — same as it would for your customers.
            </p>
          </div>

          <p className="mx-auto mt-10 max-w-lg text-sm leading-relaxed text-neutral-400" data-id="element-110" data-mp="1xizm5iq3yth0">
            That's what picks up at 9pm when you're covered in drywall dust. Not voicemail.
          </p>
        </div>
      </section>

      {/* 4. GUARANTEE */}
      <section className="border-b border-hairline" data-id="element-111" data-mp="1oggmt8lj6yzi">
        <div className="mx-auto max-w-3xl px-5 py-14 text-center sm:py-20" data-id="element-112" data-mp="1jhvk92it437w">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-emerald-500/40 bg-emerald-500/10" data-id="element-113" data-mp="1ejtzmd5qfvub">
            <ShieldCheck className="h-7 w-7 text-emerald-500" data-id="element-114" data-mp="3dveoswb98m2p" />
          </div>
          <h2 className="mt-5 text-2xl font-extrabold leading-snug text-white sm:text-3xl" data-id="element-115" data-mp="38xd466y6kep4">
            We respond to every missed call in your first 14 days, or your first month is 100% money back.
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-neutral-400" data-id="element-116" data-mp="3lf5t9iuossy3">
            We guarantee the one thing we fully control: missed-call response speed. Every response is
            timestamped and logged. If our system does not respond to every missed call within 5 minutes
            during your first 14 days, your first month is refunded, no hassle. See full terms below.
          </p>
        </div>
      </section>

      {/* 5. PROOF */}
      <section className="border-b border-hairline bg-inkAlt" data-id="element-117" data-mp="37gpj1hxm8kb7">
        <div className="mx-auto max-w-5xl px-5 py-14 sm:py-20" data-id="element-118" data-mp="37f4se3ef9lim">
          <Eyebrow data-id="element-119" data-mp="3cgk9jkdhb7vq">Built by people who've run this before</Eyebrow>
          <h2 className="mt-3 max-w-2xl text-2xl font-bold leading-snug text-white sm:text-3xl" data-id="element-120" data-mp="mqgz4osd24lw">
            This is what your calendar looks like when the AI never misses a call.
          </h2>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-neutral-400" data-id="element-121" data-mp="2163yxnu8kuc">
            Built by former Yelp reps who managed over $2 million a month in ad spend for home-service
            businesses. Below are unedited calendars and lead lists from live client accounts — names blurred.
          </p>

          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-3" data-id="element-122" data-mp="6zpp4vulxsln">
            <StatTile n="~30/mo" l="new-customer estimates booked by the AI, hands-free" data-id="element-123" data-mp="6yemizcweqw4" />
            <StatTile n="~40%" l="close rate on AI-booked estimates (real client average)" data-id="element-124" data-mp="3fdycnpi9vt6y" />
            <StatTile n="$30k+/mo" l="in booked opportunity at typical job values (estimated)" data-id="element-125" data-mp="3aev4hw6zx6ur" />
          </div>

          <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-2" data-id="element-126" data-mp="3p7oy0lj3rnif">
            {PROOF_SHOTS.map(shot => <figure key={shot.src} className={`overflow-hidden rounded-xl border border-hairline2 bg-ink ${shot.wide ? 'md:col-span-2' : ''}`} data-id="element-127" data-mp="2wpb7l703j3td">
                <img src={shot.src} alt={shot.alt} loading="lazy" className="w-full" data-id="element-128" data-mp="2rpoqgmh5d3fa" />
                <figcaption className="border-t border-hairline2 p-4 text-xs leading-relaxed text-neutral-400" data-id="element-129" data-mp="177e9n0j4jpgr">{shot.caption}</figcaption>
              </figure>)}
          </div>

          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-130" data-mp="xb1a3m0wehe3">
            <div className="rounded-lg border border-hairline bg-ink p-6" data-id="element-131" data-mp="129324wgaxr1p">
              <div className="font-eyebrow text-[10px] font-semibold uppercase text-neutral-500" data-id="element-132" data-mp="1279q11mx6emb">Real client result · Phoenix landscaping company</div>
              <p className="mt-3 text-sm leading-relaxed text-neutral-300" data-id="element-133" data-mp="1uwyvpsakr2u9"><span className="font-semibold text-white" data-id="element-134" data-mp="1gy8z0dro356f">284% more leads</span> on the same ad spend. The AI booked <span className="font-semibold text-white" data-id="element-135" data-mp="1kzu77lt9avaa">28 estimates end-to-end</span> in Yelp's Request-a-Quote flow at a <span className="font-semibold text-white" data-id="element-136" data-mp="5jbvxzynefg2">39.95% close rate</span> — response time went from 4+ hours to instant.</p>
            </div>
            <div className="rounded-lg border border-hairline bg-ink p-6" data-id="element-137" data-mp="9m8k066yj3re">
              <div className="font-eyebrow text-[10px] font-semibold uppercase text-neutral-500" data-id="element-138" data-mp="psv92mih2jz1">Real client result · residential/commercial painting company</div>
              <p className="mt-3 text-sm leading-relaxed text-neutral-300" data-id="element-139" data-mp="g20b4kvv5vxr"><span className="font-semibold text-white" data-id="element-140" data-mp="g2jqpuy1k88m">29 estimates booked in 30 days</span> — about one a day — while they were on the tools. <span className="font-semibold text-white" data-id="element-141" data-mp="b12tqaioh5xb">$15,050 closed</span> from estimates the AI booked hands-free.</p>
            </div>
          </div>
          <p className="mt-4 text-xs text-neutral-500" data-id="element-142" data-mp="13swac32nftf1">Real past-client results and screenshots, anonymized. Dollar figures shown as estimates at typical job values. Not a guarantee of the same numbers for every business.</p>

          <div className="mt-10 flex flex-col items-center gap-4" data-id="element-143" data-mp="uqmwb3869hww">
            <div className="flex flex-col gap-3 sm:flex-row" data-id="element-144" data-mp="3jjsbamk5lm71">
              <BookCTA label="Get this on your calendar" data-id="element-145" data-mp="3fhdq3vt0iacl" />
              <CallCTA data-id="element-146" data-mp="3ffu7hyjvvn7n" />
            </div>
            <div className="flex flex-wrap items-center gap-6 opacity-90" data-id="element-147" data-mp="1uzcznyjy55ka">
              <img src={HUBSPOT_BADGE} alt="HubSpot SEO certified" className="h-9 w-auto" data-id="element-148" data-mp="1ux9xkg5irlz7" />
              <img src={CHAMBER_BADGE} alt="Scottsdale Chamber of Commerce" className="h-9 w-auto" data-id="element-149" data-mp="1yylsn175wzes" />
            </div>
          </div>
        </div>
      </section>

      {/* 6. OFFER */}
      <section className="border-b border-hairline" data-id="element-150" data-mp="28rsu4ays7v9l">
        <div className="mx-auto max-w-2xl px-5 py-14 text-center sm:py-20" data-id="element-151" data-mp="2dq44cmszo7sc">
          <Eyebrow data-id="element-152" data-mp="2ionpikzrdfjn">The offer</Eyebrow>
          <h2 className="mt-3 text-4xl font-extrabold text-white sm:text-5xl" data-id="element-153" data-mp="2inmfmjuhaz3i">$1,197<span className="text-xl font-semibold text-neutral-400" data-id="element-154" data-mp="2mqisrv192302">/month</span></h2>
          <p className="mt-2 text-sm font-medium text-emerald-500" data-id="element-155" data-mp="2rpdilgxmwrto">Month to month. No contract. Cancel anytime.</p>

          <div className="mx-auto mt-7 max-w-md rounded-xl border border-hairline2 bg-inkAlt p-6 text-left" data-id="element-156" data-mp="scrkozykjr6q">
            <p className="text-center text-sm font-semibold text-white" data-id="element-157" data-mp="xajbtrre399v">Why that number is a steal:</p>
            <ul className="mt-4 space-y-2.5" data-id="element-158" data-mp="20lgptyol7p9b">
              {['Replaces a full-time receptionist ($3,000+/mo) — the AI answers every call, 24/7.', 'Replaces your CRM, review tool, and invoicing — one bill instead of five apps.', 'Catches the calls you already paid Google, Yelp & Meta to send you.', 'Built and set up with you, in your voice — live in days, not months.'].map(li => <li key={li} className="flex items-start gap-2 text-sm leading-relaxed text-neutral-300" data-id="element-159" data-mp="2em16opaxzb06">
                  <Check className="mt-0.5 h-4 w-4 flex-none text-emerald-500" strokeWidth={2.5} data-id="element-160" data-mp="2sd6a82hfknfs" />{li}
                </li>)}
            </ul>
            <p className="mt-4 border-t border-hairline2 pt-4 text-center text-sm font-bold text-emerald-400" data-id="element-161" data-mp="2ocmff5kbxvwt">
              One missed $3,000 job costs you more than a whole month of this. It pays for itself the first call it saves.
            </p>
          </div>

          <div className="mt-6" data-id="element-162" data-mp="3gy1agilr60pg"><GuaranteeBadge data-id="element-163" data-mp="3gzm1hy8uz1ik" /></div>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-neutral-500" data-id="element-164" data-mp="3gwggov79ilsn">
            <span className="inline-flex items-center gap-1.5" data-id="element-165" data-mp="370dddm0och44"><Clock className="h-3.5 w-3.5 text-emerald-500" data-id="element-166" data-mp="36zlg86rfibbq" /> Live in days, not months</span>
            <span className="inline-flex items-center gap-1.5" data-id="element-167" data-mp="3bxwv4vwkzc0t"><Users className="h-3.5 w-3.5 text-emerald-500" data-id="element-168" data-mp="2y83364ps4bmv" /> Only 5 new businesses onboarded per month</span>
          </div>
          <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center" data-id="element-169" data-mp="32yrrxca2quib">
            <BookCTA label="Book Your Free 15-Minute Call" data-id="element-170" data-mp="2oaewcgewfdoc" />
            <CallCTA data-id="element-171" data-mp="2o7sosgs0cy64" />
          </div>
        </div>
      </section>

      {/* 7. CLOSER */}
      <section className="border-b border-hairline bg-inkAlt" data-id="element-172" data-mp="2t6lzy2jtod34">
        <div className="mx-auto max-w-2xl px-5 py-20 text-center sm:py-28" data-id="element-173" data-mp="2fa8k0xuxsjqr">
          <h2 className="text-3xl font-extrabold leading-tight text-white sm:text-4xl" data-id="element-174" data-mp="2jash61fifncw">Seriously — it's time to stop letting revenue slip.</h2>
          <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-neutral-400" data-id="element-175" data-mp="2zpa1v65odofc">
            Every call that goes to voicemail is a job your competitor answers instead. Book fifteen
            minutes, or just call us right now and hear the system work.
          </p>
          <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center" data-id="element-176" data-mp="2krzkjg6cpobp">
            <BookCTA label="Book Your Call" data-id="element-177" data-mp="2grz6hwn5zf56" />
            <CallCTA data-id="element-178" data-mp="2gt0ettcspnj7" />
          </div>
          <div className="mt-6 flex justify-center" data-id="element-179" data-mp="2c07fxm40d9f1"><GuaranteeBadge data-id="element-180" data-mp="2bye47sejfz06" /></div>
        </div>
      </section>

      {/* TERMS */}
      <section id="terms" className="border-b border-hairline" data-id="element-181" data-mp="271w1ey2rqaqh">
        <div className="mx-auto max-w-3xl px-5 py-12" data-id="element-182" data-mp="2232nx5nhejqb">
          <div className="font-eyebrow text-[10px] font-semibold uppercase text-neutral-500" data-id="element-183" data-mp="2206sbnz3w751">Guarantee terms &amp; conditions</div>
          <div className="mt-3 space-y-2 text-[11px] leading-relaxed text-neutral-500" data-id="element-184" data-mp="1t1uyze6uuv8w">
            <p data-id="element-185" data-mp="8jkhdq10dd9b">The 14-day money-back guarantee applies to the first month only and to missed-call response speed only. It guarantees that Viridia's system sends a response to every missed call it receives within 5 minutes during the client's first 14 days after go-live. Response times are automatically timestamped and logged; those logs are the sole record for any guarantee claim.</p>
            <p data-id="element-186" data-mp="mgzdfdlroja9">The guarantee covers missed-call response and delivery, which Viridia controls. It does not guarantee booked jobs, revenue, close rate, or number of customers, which depend on the client's own pricing, sales, and market. The 14-day window begins at go-live, once the system is active and the client has completed required onboarding.</p>
            <p data-id="element-187" data-mp="3l9a9n23lgus">The guarantee does not apply if the client has not completed onboarding or synced their calendar or granted required access, disconnects or revokes their phone, Yelp, Google, or CRM integrations, or pauses, disables, or routes calls outside the system. One claim per client; refund of the first month's fee is the sole and entire remedy. Any one-time setup fee is non-refundable.</p>
            <p data-id="element-188" data-mp="2lxox4wgt71g6">Service is month to month; cancel anytime with 7 days' notice. The system relies on third-party platforms (the client's phone carrier, Yelp, Google, Meta, and workflow tools); Viridia is not responsible for their outages or policy changes, and the guarantee is contingent on those integrations remaining connected. Client results shown are real past-client outcomes and are not a guarantee of the same results.</p>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="px-5 py-10" data-id="element-189" data-mp="1oc11wew3ap68">
        <div className="mx-auto flex max-w-4xl flex-col items-center gap-4 text-center" data-id="element-190" data-mp="1o8wt147oj598">
          <img src={LOGO_URL} alt="Viridia Analytics" className="h-12 w-auto opacity-80" data-id="element-191" data-mp="1tacc4qqar1oz" />
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-neutral-500" data-id="element-192" data-mp="1fathn0qerov5">
            <a href={PHONE_TEL} onClick={trackLead} className="inline-flex items-center gap-1.5 text-emerald-300 hover:text-emerald-200" data-id="element-193" data-mp="1kbh6pgv2tvmq"><Phone className="h-3.5 w-3.5" data-id="element-194" data-mp="vohx43a36r5e" /> {PHONE_DISPLAY}</a>
            <span className="inline-flex items-center gap-1.5" data-id="element-195" data-mp="vqci1hlcyj9h"><Mail className="h-3.5 w-3.5" data-id="element-196" data-mp="19osn743ko1pi" /> info@viridiaanalytics.com</span>
            <span className="inline-flex items-center gap-1.5" data-id="element-197" data-mp="15mp9s9xcen1l"><MapPin className="h-3.5 w-3.5" data-id="element-198" data-mp="15lwbzygd37vl" /> Phoenix, AZ</span>
          </div>
          <a href="https://viridiaanalytics.com" target="_blank" rel="noopener noreferrer" className="text-xs text-neutral-600 underline decoration-neutral-800 underline-offset-4 hover:text-neutral-400" data-id="element-199" data-mp="1jloz7jsoj5yt">See the full Viridia Analytics site</a>
        </div>
      </footer>
    </div>;
}